import React, { useCallback, useContext, useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import { Hub } from "aws-amplify";

import { AuthContext } from "context/providers";
import { contextConstants } from "context/constants";

const SESSION_VERIFICATION_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Session expiration handler - single source of truth for auth/session invalidation.
 * Periodic verification every 5 minutes
 * Hub tokenRefresh_failure
 * Window focus (catches idle/time-passed expiration)
 * Initial verification on mount
 */
export default function SessionExpirationHandler({ signOut, children }) {
  const auth = useContext(AuthContext);
  const dispatch = auth?.dispatch;
  const isHandlingExpiration = useRef(false);

  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  const handleSessionExpiration = useCallback(() => {
    if (isHandlingExpiration.current) return;
    isHandlingExpiration.current = true;

    if (dispatch) {
      dispatch({ type: contextConstants.auth.USER_SIGNEDOUT });
    }
    signOutRef.current();

    setTimeout(() => {
      isHandlingExpiration.current = false;
    }, 1000);
  }, [dispatch]);

  const verifySession = useCallback(async () => {
    try {
      const session = await Auth.currentSession();
      const idToken = session?.getIdToken?.();
      if (!session || !idToken) {
        handleSessionExpiration();
        return false;
      }
      return true;
    } catch {
      handleSessionExpiration();
      return false;
    }
  }, [handleSessionExpiration]);

  useEffect(() => {
    verifySession();
  }, [verifySession]);

  useEffect(() => {
    const hubListener = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "tokenRefresh_failure") {
        handleSessionExpiration();
      }
    });
    return () => {
      hubListener();
    };
  }, [handleSessionExpiration]);

  useEffect(() => {
    const intervalId = setInterval(
      verifySession,
      SESSION_VERIFICATION_INTERVAL_MS
    );
    return () => clearInterval(intervalId);
  }, [verifySession]);

  useEffect(() => {
    const handleFocus = () => {
      verifySession();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [verifySession]);

  return <>{children}</>;
}
